import { lazy, Suspense, useCallback, useEffect, useState } from "react";

import CosmosScreens from "./CosmosScreens";
import { isEmpty } from "@groww-tech-pvt/ella";
import { AOF_STATUS, JOURNEY_TYPES, SEARCH_PARAMS } from "core/constants";

import { getSearchParams, useNavigateToRoute } from "core/hooks/useGetParam";

const JourneySelectionScreen = lazy(() => import('./JourneySelectionScreen'));
import { ROUTES } from "src/utils/constants/routes";
import { ValueOf } from "src/utils/types";
import { getIsDomain915 } from "src/utils/config/domainConfig";
import { AppQr } from "..";
import { useHoistValue } from "src/utils/adapter/hoist";
import { getDigilockerParams } from "core/utils/digilockerParamCache";
import { useUserDataStore } from "src/utils/store/UserData";
import OnbLoader from "src/components/OnbLoader";

export const CURRENT_SCREEN = {
  JOURNEY_SELECTION_SCREEN: 'JOURNEY_SELECTION_SCREEN',
  COSMOS_SCREENS: 'COSMOS_SCREENS',
  LOADING_SCREEN: 'LOADING_SCREEN',
  APP_QR_SCREEN: 'APP_QR_SCREEN'
} as const;

const FlowNavigator = () => {
  const { digio_doc_id, journeyType, status, success } = getSearchParams();
  const is915Domain = getIsDomain915();
  const { userData } = useUserDataStore();
  const { navigateToRoute } = useNavigateToRoute();
  const [currentScreen, setCurrentScreen] = useState<ValueOf<typeof CURRENT_SCREEN>>(CURRENT_SCREEN.LOADING_SCREEN);
  const cachedDigilockerParams = getDigilockerParams();

  const { hoistValue: isMainOnboardingEnabled, isHoistLoading: isMainOnboardingHoistLoading } = useHoistValue({
    hoistKey: 'web-main-onboarding',
    defaultValue: false
  });

  const switchToAppropriateScreen = useCallback(() => {
    //1. if digilocker params are present, then navigate to cosmos screens
    if (!isEmpty(digio_doc_id) && cachedDigilockerParams) {
      navigateToRoute(ROUTES.ONBOARDING, {
        journeyType: cachedDigilockerParams[SEARCH_PARAMS.JOURNEY_TYPES],
        dataType: cachedDigilockerParams[SEARCH_PARAMS.DATA_TYPE],
        status: status || (success === 'true' ? 'success' : 'failed'),
        digio_doc_id: digio_doc_id,
      });
      setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
      return;
    }

    //2. if 915 domain, then navigate to cosmos screens directly
    if (is915Domain) {
      const isBaseOnboardingPath = window.location.pathname === ROUTES.ONBOARDING;
      if (isBaseOnboardingPath && isEmpty(journeyType) && isEmpty(digio_doc_id)) {
        navigateToRoute(ROUTES.ONBOARDING, { journeyType: JOURNEY_TYPES.MAIN_ONBOARDING });
        setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
        return;
      }
      setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
      return;
    }

    //3. if user is already onboarded, then navigate to cosmos screens
    if (!isEmpty(userData) && userData?.aof_status === AOF_STATUS.COMPLETED && (isEmpty(journeyType) || journeyType === JOURNEY_TYPES.MAIN_ONBOARDING)) {
      setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
      return;
    }

    //4. if journey type is present, then navigate to cosmos screens
    if (!isEmpty(journeyType)) {
      setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
      return;
    }

    //5. if main onboarding is not enabled, then navigate to app qr screen
    if (!isMainOnboardingHoistLoading) {
      if (!isMainOnboardingEnabled) {
        setCurrentScreen(CURRENT_SCREEN.APP_QR_SCREEN);
        return;
      }
      setCurrentScreen(CURRENT_SCREEN.JOURNEY_SELECTION_SCREEN);
      return;
    }

    setCurrentScreen(CURRENT_SCREEN.LOADING_SCREEN);
  }, [
    digio_doc_id,
    cachedDigilockerParams,
    is915Domain,
    journeyType,
    userData,
    status,
    success,
    isMainOnboardingEnabled,
    isMainOnboardingHoistLoading,
  ]);

  useEffect(() => {
    switchToAppropriateScreen();
  }, [switchToAppropriateScreen]);

  const getLoadingScreen = () => {
    return <div className="absolute-center height100">
      <OnbLoader />
    </div>;
  };

  switch (currentScreen) {
    case CURRENT_SCREEN.APP_QR_SCREEN:
      return <AppQr />;

    case CURRENT_SCREEN.JOURNEY_SELECTION_SCREEN:
      return (
        <Suspense fallback={getLoadingScreen()}>
          <JourneySelectionScreen />
        </Suspense>
      );
    case CURRENT_SCREEN.COSMOS_SCREENS:
      return <CosmosScreens />;
    default:
      return getLoadingScreen();
  }
};


export default FlowNavigator;
